import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { interviewTitle } from '@/components/CandidateInterviewHeader/CandidateInterviewHeader.component';
import { CHALLENGE_TYPE_INPUT_PLACEHOLDER } from '@/components/ChallengeTypeInput/ChallengeTypeInput.container';
import Loading from '@/components/general/Loading';
import candidatePagesContext from '@/contexts/candidatePagesContext';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import candidatesServices, { PartialCandidateToSubmit } from '@/services/candidates';
import interviewsServices, {
  CreateInterviewParams,
  DeleteInterviewParams,
  TechnicalChallengeParams,
  UpdateInterviewParams,
  CreateTechnicalInterviewProps,
} from '@/services/interviews';
import { FetchApiIds } from '@/types';
import { InterviewQuestionType, InterviewType } from '@/types/api';
import { capitalizeString, onScrollToSmoothly } from '@/utils/global';
import {
  InterviewTypes,
  defaultInterviewsStatuses,
  interviewsThatHasNotes,
} from '@/utils/interviews';
import CandidateInterviews from './CandidateInterviews.component';

const defaultInterviewType: InterviewStateType = {
  openedModal: '',
  type: InterviewTypes.PRE_SCREENING,
  name: '',
};

const defaultEditingInterview = {
  name: '',
  id: '',
};

export type AiGeneratedOverview = {
  overview: string;
  interviewId: string;
  interviewTitle: string;
  reviewId: string;
  reviewerName?: string;
  updatedAt: string;
};

export type EditingInterviewType = typeof defaultEditingInterview;

export type InterviewStateType = {
  name: string;
  openedModal: string;
  type: InterviewTypes;
};

const EXPANDED_INTERVIEWS_STORAGE_KEY = 'expandedInterviewsStorage';

// pattern for stringified {[string]: string[]|undefined}
const STORED_EXPANDED_INTERVIEWS_PATTERN =
  /^{("[\w|\\-]*":\s*(\[("[\w|\\-]*",\s*)*("[\w|\\-]*")?]|undefined),\s*)*("[\w|\\-]*":\s*(\[("[\w|\\-]*",\s*)*("[\w|\\-]*")?]|undefined))?}$/;

const getStoredExpandedInterviews = (): Record<string, string[]> | undefined => {
  const storedData = localStorage.getItem(EXPANDED_INTERVIEWS_STORAGE_KEY);

  let match = false;

  if (storedData) {
    match = STORED_EXPANDED_INTERVIEWS_PATTERN.test(storedData);
  }

  if (storedData && match) {
    return JSON.parse(storedData);
  }

  return undefined;
};

const CandidateInterviewsPage = function (): JSX.Element {
  const toast = useToastContext();
  const { id } = useParams();
  const { profile } = useProfile();
  const { hash } = useLocation();
  const { candidate, refetchCandidate, isLoadingCandidate } = useContext(candidatePagesContext);
  const [interviewType, setInterviewType] = useState<InterviewStateType>(defaultInterviewType);
  const [interviewIdToDelete, setInterviewIdToDelete] = useState('');
  const [interviewIdToSend, setInterviewIdToSend] = useState('');
  const [editingInterview, setEditingInterview] = useState(defaultEditingInterview);
  const [autoScrollInterview, setAutoScrollInterview] = useState<string>();

  const localExpandedInterviews = getStoredExpandedInterviews();

  const [expandedInterviews, setExpandedInterviews] = useState<Record<string, boolean>>(
    id && localExpandedInterviews?.[id]
      ? localExpandedInterviews[id].reduce((obj, interviewId) => {
          return {
            ...obj,
            [interviewId]: true,
          };
        }, {})
      : {}
  );

  const [technicalValidationTypeToSend, setTechnicalValidationTypeToSend] = useState(
    CHALLENGE_TYPE_INPUT_PLACEHOLDER
  );

  const [languageUsedByCandidate, setLanguageUsed] = useState([{ label: '', value: '' }]);

  const onChangeLanguageUsed = (event: any): void => {
    const { value } = event.target;
    const { label } = event.target;

    // Handle both single value and array of values
    if (Array.isArray(value)) {
      setLanguageUsed(
        value.map((val, index) => ({
          value: val,
          label: Array.isArray(label) ? label[index] : label,
        }))
      );
    } else {
      setLanguageUsed([{ label, value }]);
    }
  };

  // Extract skillId from the selected language/technology
  const skillId = languageUsedByCandidate?.[0]?.value || undefined;

  const interviewResponse = useApiFetch([FetchApiIds.CANDIDATE_INTERVIEW, id], async () =>
    interviewsServices.getCandidateInterviews({ candidateId: id as string })
  );

  const updateCandidateMutation = useApiSubmit<any, PartialCandidateToSubmit>(
    candidatesServices.updateCandidateIndividuallyById
  );

  const createInterviewMutation = useApiSubmit<InterviewType, CreateInterviewParams>(
    interviewsServices.createInterview
  );

  const deleteInterviewMutation = useApiSubmit<any, DeleteInterviewParams>(
    interviewsServices.deleteInterview
  );

  const updateInterviewMutation = useApiSubmit<any, UpdateInterviewParams>(
    interviewsServices.updateInterview
  );

  const sendInterviewMutation = useApiSubmit<any, DeleteInterviewParams & TechnicalChallengeParams>(
    interviewsServices.sendInterview
  );

  const getIfCandidateStatusIsUpdated = (
    candidateStatus: string | null,
    currentInterviewType: string
  ): boolean => {
    const currentDefaultInterviewStatus = defaultInterviewsStatuses[currentInterviewType];
    return candidateStatus !== currentDefaultInterviewStatus;
  };

  const getIfInterviewHasAnswers = (interviewQuestions: InterviewQuestionType[]): boolean => {
    if (!interviewQuestions.length) {
      return false;
    }

    for (const questionIndex in interviewQuestions) {
      if (interviewQuestions[questionIndex].answer) {
        return true;
      }
    }

    return false;
  };

  const getIfCanAddNewReview = useCallback((interview: InterviewType): boolean => {
    const hasAnswers = getIfInterviewHasAnswers(interview.questions);
    const hasNotes = !!interview.notes.length;
    const hasReviews = !!interview.reviews.length;

    return hasAnswers || hasNotes || hasReviews;
  }, []);

  const getIfCanSubmitInterview = useCallback(
    (interview: InterviewType): boolean => {
      const candidateHasEmail = !!candidate?.email;
      const hasQuestions = !!interview.questions.length;
      const isTechnicalTypeChosen =
        !!technicalValidationTypeToSend &&
        technicalValidationTypeToSend !== CHALLENGE_TYPE_INPUT_PLACEHOLDER;

      return candidateHasEmail && (hasQuestions || isTechnicalTypeChosen);
    },
    [candidate?.email, technicalValidationTypeToSend]
  );

  const getIfShouldDisplayReviewsManager = (interview: InterviewType): boolean => {
    if (interview.type === InterviewTypes.TECHNICAL_CHALLENGE) {
      return false;
    }

    if (interview.type === InterviewTypes.PRE_SCREENING) {
      return interview.isAlreadySent;
    }

    return true;
  };

  const getIfShouldDisplayChallengeProject = (interview: InterviewType): boolean => {
    const isTechnicalChallenge = interview.type === InterviewTypes.TECHNICAL_CHALLENGE;
    const hasGitlabUrl = !!interview.technicalChallenge?.gitlabProjectUrl;
    const hasGithubUrl = !!interview.technicalChallenge?.githubProjectUrl;
    const hasTechnicalVideo = !!interview.technicalChallenge?.technicalVideoUrl;
    const hasTechnicalDesign = !!interview.technicalChallenge?.technicalDocumentTitle;

    return (
      isTechnicalChallenge &&
      (hasGitlabUrl || hasGithubUrl) &&
      hasTechnicalVideo &&
      hasTechnicalDesign
    );
  };

  const getInterviewManagers = useCallback((interview: InterviewType): any => {
    const shouldDisplayQuestionsManager = interview.type === InterviewTypes.PRE_SCREENING;
    const shouldDisplayNotesManager = interviewsThatHasNotes.includes(interview.type);

    const shouldDisplayReviewsManager = getIfShouldDisplayReviewsManager(interview);
    const shouldDisplayAssignedInterviewReviewsManager =
      (interview.type === 'TECHNICAL_CHALLENGE' && !!interview.technicalChallenge) ||
      interview.type === 'TEAM_INTERVIEW';

    const shouldDisplayTechnicalValidationReviewsManager =
      interview.type === InterviewTypes.TECHNICAL_CHALLENGE;
    const shouldDisplayChallengeTypeInput =
      shouldDisplayTechnicalValidationReviewsManager && !interview.technicalChallenge;
    const shouldDisplayChallengeProject = getIfShouldDisplayChallengeProject(interview);

    return {
      shouldDisplayQuestionsManager,
      shouldDisplayReviewsManager,
      shouldDisplayNotesManager,
      shouldDisplayAssignedInterviewReviewsManager,
      shouldDisplayChallengeTypeInput,
      shouldDisplayChallengeProject,
      shouldDisplayTechnicalValidationReviewsManager,
    };
  }, []);

  const interviews = useMemo(() => {
    return (interviewResponse?.data ?? []).map((interview) => ({
      ...interview,
      ...getInterviewManagers(interview),
      canAddNewReview: getIfCanAddNewReview(interview),
      canSubmitInterview: getIfCanSubmitInterview(interview),
      hasReviews: !!interview.reviews.length,
      isCandidateStatusUpdated: getIfCandidateStatusIsUpdated(
        interview.person.status,
        interview.type
      ),
    }));
  }, [
    getIfCanAddNewReview,
    getIfCanSubmitInterview,
    getInterviewManagers,
    interviewResponse?.data,
  ]);

  const aiGeneratedOverview = useMemo<AiGeneratedOverview | undefined>(() => {
    let latestOverview: (AiGeneratedOverview & { timestamp: number }) | undefined;

    interviews.forEach((interview) => {
      interview.reviews.forEach((review) => {
        if (review.aiGeneratedOverview) {
          const reviewTimestamp = new Date(review.updatedAt).getTime();

          if (!latestOverview || reviewTimestamp > latestOverview.timestamp) {
            latestOverview = {
              overview: review.aiGeneratedOverview,
              interviewId: interview.id,
              interviewTitle: interview.title,
              reviewId: review.id,
              reviewerName: review.user.name,
              updatedAt: review.updatedAt,
              timestamp: reviewTimestamp,
            };
          }
        }
      });
    });

    if (!latestOverview) {
      return undefined;
    }

    const { timestamp, ...overviewMeta } = latestOverview;

    return overviewMeta;
  }, [interviews]);

  const tableOfContentOptions = useMemo(() => {
    const interviewsSortedByDescDate = interviews.sort(
      (firstInterview, secondInterview) =>
        new Date(firstInterview.createdAt).getTime() - new Date(secondInterview.createdAt).getTime()
    );

    return interviewsSortedByDescDate.map((interview) => ({
      name: interview.title,
      id: interview.id,
      scrollTo: () => onScrollToSmoothly(interview.id),
    }));
  }, [interviews]);

  const onCreateInterview = async (
    name: string,
    generateQuestionsByAI?: boolean
  ): Promise<void> => {
    try {
      const createdInterview = await createInterviewMutation.mutateAsync({
        userId: profile.id || '',
        candidateId: id as string,
        title: name,
        type: interviewType.type as InterviewTypes,
        generateQuestionsByAI,
      });
      setAutoScrollInterview(createdInterview.id);
      setInterviewType(defaultInterviewType);
      interviewResponse.refetch();
      toast.showSuccessFeedback('The interview has been created.');
    } catch {
      toast.showErrorFeedback('There was a problem to create the interview.');
    }
  };

  const onUpdateInterview = async (name: string): Promise<void> => {
    try {
      await updateInterviewMutation.mutateAsync({
        interviewId: editingInterview.id,
        candidateId: id as string,
        title: name,
      });
      setEditingInterview(defaultEditingInterview);
      interviewResponse.refetch();
      toast.showSuccessFeedback('The interview has been updated.');
    } catch {
      toast.showErrorFeedback('There was a problem to update the interview.');
    }
  };

  const onCancelNewInterview = (): void => {
    setInterviewType(defaultInterviewType);
  };

  const onAddNewInterview = (): void => {
    setInterviewType((prev) => ({
      ...prev,
      openedModal: 'TYPE',
    }));
  };

  const onEditInterviewName = (interviewName, interviewId): void => {
    setEditingInterview({ name: interviewName, id: interviewId });
  };

  const onSelectInterviewType = (type: InterviewTypes): void => {
    const formatted = capitalizeString(type);
    setInterviewType((prev) => ({
      ...prev,
      type,
      name: formatted,
      openedModal: 'NAME',
    }));
  };

  const selectInterviewToSend = (interviewId: string, type: InterviewTypes): void => {
    setInterviewIdToSend(interviewId);
    setInterviewType({ ...interviewType, type });
  };

  const onSubmitInterview = async (): Promise<void> => {
    try {
      const submitObject = {
        candidateId: id ?? '',
        interviewId: interviewIdToSend,
        skillId,
      };

      if (technicalValidationTypeToSend !== CHALLENGE_TYPE_INPUT_PLACEHOLDER) {
        Object.assign(submitObject, { technicalChallengeId: technicalValidationTypeToSend });
      }

      await sendInterviewMutation.mutateAsync(submitObject);
      interviewResponse.refetch();
      setInterviewIdToSend('');
      toast.showSuccessFeedback('The interview has been sent.');
    } catch {
      toast.showErrorFeedback('There was a problem to send the interview.');
    }
  };

  const selectInterviewToDelete = (interviewId: string): void => {
    setInterviewIdToDelete(interviewId);
  };

  const onDeleteInterview = async (): Promise<void> => {
    try {
      await deleteInterviewMutation.mutateAsync({
        candidateId: id as string,
        interviewId: interviewIdToDelete,
      });

      interviewResponse.refetch();
      setInterviewIdToDelete('');
      toast.showSuccessFeedback('The interview has been deleted.');
    } catch {
      toast.showErrorFeedback('There was a problem to delete the interview.');
    }
  };

  const onChangeEmailAddress = async (email: string): Promise<void> => {
    try {
      await updateCandidateMutation.mutateAsync({
        candidate: {
          id,
          email,
        },
      });
      refetchCandidate();
      toast.showSuccessFeedback('The email has been updated.');
    } catch {
      toast.showErrorFeedback('There was a problem to update the email.');
    }
  };

  const onRefetchInterview = (): void => {
    interviewResponse.refetch();
  };

  const getMessageToConfirmSending = (interviewState: InterviewStateType): string => {
    if (interviewTitle[interviewState.type]) {
      const { firstName, lastName } = candidate || {};
      return `${interviewTitle[interviewState.type]} will be sent to ${firstName}${
        lastName ? ` ${lastName}` : ''
      }. Make sure the right challenge is selected`;
    }

    return '';
  };

  const messageToConfirmSending = getMessageToConfirmSending(interviewType);

  const onChooseChallengeType = (event: any): void => {
    const { value } = event.target;

    setTechnicalValidationTypeToSend(value);
  };

  const isPageLoading = isLoadingCandidate || interviewResponse.isLoading;

  const onToggleCollapseInterview = useCallback(
    (interviewId: string, isOpen: boolean): void => {
      setExpandedInterviews({ ...expandedInterviews, [interviewId]: isOpen });

      if (!id) return;

      let storedExpandedInterviews = getStoredExpandedInterviews();

      if (storedExpandedInterviews && isOpen) {
        storedExpandedInterviews[id] = [...(storedExpandedInterviews[id] || []), interviewId];
      } else if (isOpen) {
        storedExpandedInterviews = {
          [id]: [interviewId],
        };
      } else if (storedExpandedInterviews) {
        storedExpandedInterviews[id] = (storedExpandedInterviews[id] || []).filter(
          (item) => item !== interviewId
        );

        if (storedExpandedInterviews[id].length === 0) {
          delete storedExpandedInterviews[id];
        }
      }

      if (!storedExpandedInterviews || Object.keys(storedExpandedInterviews).length === 0) {
        localStorage.removeItem(EXPANDED_INTERVIEWS_STORAGE_KEY);
      } else {
        localStorage.setItem(
          EXPANDED_INTERVIEWS_STORAGE_KEY,
          JSON.stringify(storedExpandedInterviews)
        );
      }
    },
    [setExpandedInterviews, id, expandedInterviews]
  );

  useEffect(() => {
    if (hash && candidate) {
      const interviewId = hash.replace('#', '');
      if (!expandedInterviews[interviewId]) {
        onToggleCollapseInterview(interviewId, true);
      }

      setTimeout(() => {
        onScrollToSmoothly(interviewId);
      }, 500);
    }
  }, [hash, candidate, onToggleCollapseInterview, expandedInterviews]);

  const createTechnicalInterviewMutation = useApiSubmit<void, CreateTechnicalInterviewProps>(
    interviewsServices.createTechnicalInterview
  );

  const onCreateTechnicalInterview = async (
    interviewId: string,
    onSuccess?: () => void
  ): Promise<void> => {
    try {
      await createTechnicalInterviewMutation.mutateAsync({
        interviewId,
        technicalChallengeId: technicalValidationTypeToSend,
        skillId, // Send skillId instead of languageUsedByCandidate
      });
      await interviewResponse.refetch();
      toast.showSuccessFeedback('Technical interview created successfully.');
      onSuccess?.();
    } catch (error) {
      toast.showErrorFeedback('There was a problem to create the interview.');
    }
  };

  if (isPageLoading) {
    return <Loading fullPage />;
  }

  return (
    <CandidateInterviews
      languageUsedByCandidate={languageUsedByCandidate.map((item) => item.value).filter(Boolean)}
      aiGeneratedOverview={aiGeneratedOverview}
      onChangeLanguageUsed={onChangeLanguageUsed}
      onChooseChallengeType={onChooseChallengeType}
      technicalValidationTypeToSend={technicalValidationTypeToSend}
      onRefetchInterview={onRefetchInterview}
      tableOfContentOptions={tableOfContentOptions}
      onChangeEmailAddress={onChangeEmailAddress}
      editingInterview={editingInterview}
      onUpdateInterview={onUpdateInterview}
      onEditInterviewName={onEditInterviewName}
      onDeleteInterview={onDeleteInterview}
      openDeleteDialog={!!interviewIdToDelete}
      selectInterviewToDelete={selectInterviewToDelete}
      interviews={interviews}
      onSelectInterviewName={onCreateInterview}
      onSelectInterviewType={onSelectInterviewType}
      onCancelNewInterview={onCancelNewInterview}
      interviewType={interviewType as InterviewStateType}
      onClickAddNew={onAddNewInterview}
      candidate={candidate}
      onSubmitInterview={onSubmitInterview}
      selectInterviewToSend={selectInterviewToSend}
      onCreateInterview={onCreateTechnicalInterview}
      openSendDialog={!!interviewIdToSend}
      messageToConfirmSending={messageToConfirmSending}
      isSendingInterview={
        sendInterviewMutation.isLoading || createTechnicalInterviewMutation.isLoading
      }
      expandedInterviews={expandedInterviews}
      onToggleCollapseInterview={onToggleCollapseInterview}
      autoScrollInterview={autoScrollInterview}
      onAutoScroll={() => setAutoScrollInterview(undefined)}
    />
  );
};

export default CandidateInterviewsPage;
